import { useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  DURATION_UNITS,
  durationValue,
  formatDuration,
  formatDurationNumber,
  formatDurationShort,
  formatDurationSigned,
  parseDuration,
  type DurationUnit,
} from '@forecastify/shared';
import { DurationContext } from './duration-context';
import { useI18n } from './i18n';

export { useDurationUnit } from './duration-context';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · the reader's duration unit: hours or man-days.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` DU-1. One person's choice, stored in `user_preferences.duration_unit` and
 * applied by `PreferencesBoot` the way the theme and the language are; every screen formats and
 * parses a duration through `useDuration()`, never through `formatHours` directly.
 *
 * ⚠️ **The labels follow without a call-site change.** `I18nProvider` reads this context: in days
 * mode `t('time.column.hours')` answers with `time.column.hours.days` when the catalogue has one
 * (`i18n/duration.ts`), so a header or a template that names the unit flips with the figures.
 *
 * ⚠️ **ABOVE the i18n provider** — the provider reads the unit, and this reads nothing of it.
 * The formatting functions take the locale at call time through `useDuration`.
 */

/** ⚠️ The localStorage hint, read only as the first frame's guess — `preferences-boot.tsx`. */
export const DURATION_HINT_KEY = 'forecastify.durationUnit';

function storedUnit(): DurationUnit | undefined {
  try {
    const stored = globalThis.localStorage.getItem(DURATION_HINT_KEY);
    return DURATION_UNITS.find((unit) => unit === stored);
  } catch {
    return undefined;
  }
}

export function rememberDurationUnit(unit: DurationUnit): void {
  try {
    globalThis.localStorage.setItem(DURATION_HINT_KEY, unit);
  } catch {
    /* No hint, so the next cold start shows hours until the account answers. */
  }
}

export function DurationProvider({
  unit: pinned,
  children,
}: {
  /** ⚠️ Pins the unit, for a suite — the same contract as `I18nProvider`'s `locale`. */
  readonly unit?: DurationUnit;
  readonly children: ReactNode;
}): ReactNode {
  const [unit, setState] = useState<DurationUnit>(pinned ?? storedUnit() ?? 'hours');
  const setUnit = useCallback(
    (next: DurationUnit) => {
      if (pinned !== undefined) return;
      setState(next);
      rememberDurationUnit(next);
    },
    [pinned],
  );
  const value = useMemo(() => ({ unit: pinned ?? unit, setUnit }), [pinned, unit, setUnit]);
  return <DurationContext.Provider value={value}>{children}</DurationContext.Provider>;
}

export interface Duration {
  readonly unit: DurationUnit;
  readonly setUnit: (unit: DurationUnit) => void;
  /** `7:30` / `0,94 nap` — self-describing, for anywhere. */
  readonly fmt: (hours: number | string) => string;
  /** `7:30` / `0,94` — under a header (or inside words) that already name the unit. */
  readonly fmtNum: (hours: number | string) => string;
  /** `7:30` / `0,94n` — a narrow cell, too tight for `nap`; it parses back. */
  readonly fmtShort: (hours: number | string) => string;
  /** `+3:30` / `−0,5 nap`. */
  readonly fmtSigned: (hours: number | string) => string;
  /** Typed text to HOURS, in the reader's unit; `null` when it is not a duration. */
  readonly parse: (text: string | number) => number | null;
  /** Hours as a plain number in the reader's unit — a chart's value, an input's draft. */
  readonly value: (hours: number) => number;
  /** A catalogue string; the `.days` sibling in days mode. ⚠️ `t` already does this itself. */
  readonly label: ReturnType<typeof useI18n>['t'];
}

/**
 * ⚠️ **Works outside a `DurationProvider`, in hours** — a component under test that never mounted
 * one renders exactly as it did before the unit existed.
 */
export function useDuration(): Duration {
  const state = useContext(DurationContext);
  const { locale, t } = useI18n();
  const unit = state?.unit ?? 'hours';
  const setUnit = state?.setUnit;
  return useMemo<Duration>(
    () => ({
      unit,
      setUnit: setUnit ?? (() => undefined),
      fmt: (hours) => formatDuration(hours, unit, locale),
      fmtNum: (hours) => formatDurationNumber(hours, unit, locale),
      fmtShort: (hours) => formatDurationShort(hours, unit, locale),
      fmtSigned: (hours) => formatDurationSigned(hours, unit, locale),
      parse: (text) => parseDuration(text, unit),
      value: (hours) => durationValue(hours, unit),
      label: t,
    }),
    [unit, setUnit, locale, t],
  );
}
